<template>
  <dropdown
    ref="dropdownRef"
    :is-disabled="isDisabled"
    :title="dropdownTitle"
    class="assignment-picker"
    @open="handleDropdownOpen()"
  >
    <div :class="modifiers" class="assignment-picker__selected-person">
      <avatar :person="selectedPerson" :size="avatarSize" />
      <span v-if="!hideName"><strong v-if="title">{{ title }}:&nbsp;</strong>{{getName(selectedPerson)}}</span>
      <i class="ddi ddi-caret-down" />
    </div>
    <template #content>
      <div>
        <sleek-filter-input
          v-if="showFilterInput"
          ref="sleekFilterInputRef"
          v-model="filterQuery"
          placeholder="Search by name…"
          show-small-text
        />
        <ul>
          <li
            v-for="person in filteredOptions"
            :key="person.id"
            @click="handleSelection(person.id)"
          >
            <avatar :person="person" :size="avatarSize" />
            <span :class="{ 'selected': isSelected(person.id) }">
              {{ person.name }}
              <i v-if="person.isGuest" class="ddi ddi-warning" title="Guest user" />
            </span>
          </li>
        </ul>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */
import SearchableDropdownMixin from '@/mixins/searchable-dropdown.mixin'

/* Helpers */
import AssigneeAssistant from '@/helpers/assignee-assistant'

/* Types */
import { IPersonWithEmail } from '@/api/types/response.types'

export default defineComponent({
  name: 'AssignmentPicker',

  extends: SearchableDropdownMixin,

  props: {
    options: {
      type: Array as () => IPersonWithEmail[],
      default: (): IPersonWithEmail[] => [],
    },
    hideName: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'small',
    },
    /* Used for Jobs, but abstracting name to make this more abstract-y */
    title: {
      type: String,
      default: null,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      avatarSize: 19,
    }
  },

  computed: {
    totalOptionsCount(): number {
      return this.options.length
    },
    filteredOptions(): IPersonWithEmail[] {
      const result = AssigneeAssistant.getFullAssigneeOptions(
        this.options,
        this.$permissions,
      )

      if (this.filterQuery.length === 0) {
        return result
      }

      return result.filter(person => {
        return person.name
          .toLowerCase()
          .includes(this.filterQuery.toLowerCase())
      })
    },
    /* Null, if unassigned */
    selectedPerson(): IPersonWithEmail {
      return this.options.find(p => p.id === this.internalSelectedID)
    },
    loggedInPerson(): IPersonWithEmail {
      return this.options.find(p => this.$permissions.isLoggedInUser(p.id))
    },
    dropdownTitle(): string {
      return this.title ? this.title : 'People'
    },
    modifiers(): Array<string> {
      const selectors = [this.size]

      if (this.isDisabled) {
        selectors.push('disabled')
      }
      return selectors
    },
  },

  methods: {
    getName(person: IPersonWithEmail) {
      return person ? person.name : 'Unassigned'
    },
  },
})
</script>

<style lang="scss">
.assignment-picker {
  &__selected-person {
    background-color: var(--color-background-header);
    border: solid 1px $color--gray-30;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    @include flex-row-horizontal-spacing(6px);

    &.small {
      height: 29px;
    }

    &.medium {
      height: 33px;
    }

    &.large {
      height: 40px;
    }

    > i {
      color: var(--color-text-third);
      margin-left: auto;
    }

    span {
      @include lettering--p-sm;
      @include single-line-ellipsis;
    }

    &:hover,
    &:active {
      @include media-breakpoint-up(md) {
        background-color: var(--color-background-secondary);
      }
    }

    &.disabled {
      cursor: not-allowed;
      background-color: var(--color-background-secondary);
      opacity: 0.8;
    }

    .ddi-caret-down {
      @include dropdown-caret-size-color;
    }
  }

  .dropdown_trigger {
    width: 100%;
  }

  .dropdown__popper {
    min-width: 240px;
    max-width: 240px;
  }

  .ddi-warning {
    color: $color--orange-40 !important;
  }
}
</style>
